import React, { useEffect, useState } from "react";
import useReveal from "@/hooks/useReveal";
import { Star, Quotes } from "@phosphor-icons/react";

const CLIENTS = [
  { name: "Wellcare", subtitle: "Health Group" },
  { name: "Aroma & Co.", subtitle: "D2C Brand" },
  { name: "Nexus", subtitle: "Academy" },
  { name: "Modo", subtitle: "Retail" },
  { name: "Blue Meadow", subtitle: "LLC" },
  { name: "Sunrise", subtitle: "Café" },
  { name: "Delta", subtitle: "Trading" },
  { name: "Sable & Co.", subtitle: "Property" },
  { name: "Kite Logistics", subtitle: "Fleet" },
  { name: "Northline", subtitle: "Bank" },
  { name: "Vaidyam", subtitle: "Clinics" },
  { name: "Arcadia", subtitle: "Hotels" },
];

const QUOTES = [
  { q: "MedFlow paid for itself within one quarter. The claims team feels three times faster.", who: "Rahman N.", role: "COO, Wellcare Group" },
  { q: "One partner for the site, the logistics, and the growth engine — no finger-pointing.", who: "Aisha J.", role: "Founder, Aroma & Co." },
  { q: "The parent app changed our reputation in the market overnight.", who: "Karan M.", role: "Director, Nexus Academy" },
  { q: "We finally trust our numbers. That is worth more than any dashboard.", who: "Neel S.", role: "CFO, Modo Retail" },
  { q: "Fixed price, shipped on the day they said they would. Almost too rare to believe.", who: "Priya D.", role: "Head of Ops, Blue Meadow LLC" },
];

const ROW = [...CLIENTS, ...CLIENTS];

export default function TrustLogos() {
  const ref = useReveal();
  const [qi, setQi] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setQi((v) => (v + 1) % QUOTES.length), 5500);
    return () => clearInterval(id);
  }, []);
  const q = QUOTES[qi];

  return (
    <section ref={ref} data-testid="trust-logos" className="reveal py-14 md:py-16 bg-white border-y border-[color:var(--line)] relative overflow-hidden">
      <div className="container-x">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <span className="w-6 h-px bg-[color:var(--brand)]" />
            <div className="eyebrow">Trusted by teams shipping in production</div>
          </div>
          <div className="flex items-center gap-2" data-testid="trust-rating">
            {[1,2,3,4,5].map(i => <Star key={i} size={14} weight="fill" className="text-[color:var(--brand)]" />)}
            <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-600 ml-2">4.9 · 42 clients across UAE · India · EU</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="flex gap-6 md:gap-10 marquee-track">
          {ROW.map((c, i) => (
            <div
              key={i}
              data-testid={`trust-logo-${i}`}
              className="shrink-0 flex items-center gap-3 border border-[color:var(--line)] hover:border-[color:var(--brand)] transition-colors px-6 py-4 group"
              aria-label={c.name}
            >
              <span className="w-8 h-8 border-2 border-[color:var(--ink)] flex items-center justify-center font-display font-black text-sm group-hover:bg-[color:var(--brand)] group-hover:border-[color:var(--brand)] group-hover:text-white transition-colors">
                {c.name[0]}
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-display font-bold text-lg tracking-tight">{c.name}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{c.subtitle}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Rotating testimonial quote */}
      <div className="container-x mt-10">
        <div data-testid="trust-quote" key={qi} className="quote-fade max-w-4xl mx-auto text-center px-4">
          <Quotes size={22} weight="fill" className="text-[color:var(--brand)] mx-auto" />
          <p className="mt-3 font-display text-xl md:text-2xl leading-snug text-neutral-800">
            &ldquo;{q.q}&rdquo;
          </p>
          <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
            — <span data-testid="trust-quote-who">{q.who}</span> · {q.role}
          </div>
          <div className="mt-5 flex justify-center gap-2">
            {QUOTES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setQi(idx)}
                data-testid={`trust-quote-dot-${idx}`}
                aria-label={`Show testimonial ${idx + 1}`}
                className={`h-1 transition-all ${idx === qi ? "bg-[color:var(--brand)] w-8" : "bg-neutral-300 w-3 hover:bg-neutral-400"}`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .marquee-track { width: max-content; animation: marquee 40s linear infinite; }
        .marquee-track:hover { animation-play-state: paused; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .quote-fade { animation: qfade 0.5s ease; }
        @keyframes qfade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .marquee-track { animation: none; } .quote-fade { animation: none; } }
      `}</style>
    </section>
  );
}
